import { useEffect, useState } from "react";
import { useAdminUsers, useSetUserRole, type AdminUser, useAuthStatus } from "../../lib/queries";

export function AdminUsers() {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(id);
  }, [q]);

  const { data: users = [], isPending } = useAdminUsers(debouncedQ);
  const setRole = useSetUserRole();
  const { user: me } = useAuthStatus();

  return (
    <div style={{ padding: "16px 14px 32px" }}>
      <div style={{
        position: "sticky", top: 0, zIndex: 2,
        background: "var(--ma-paper)", paddingBottom: 8,
        marginBottom: 6,
      }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search by email / name / handle"
          style={inputStyle}
        />
        <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.08em", color: "var(--ma-ink-soft)", marginTop: 6 }}>
          {users.length} {users.length === 1 ? "user" : "users"}
        </div>
      </div>

      {isPending && (
        <div className="ma-mono" style={{ fontSize: 11, color: "var(--ma-ink-soft)", textAlign: "center", padding: 24 }}>
          loading users…
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {users.map((u) => (
          <UserRow
            key={u.id}
            user={u}
            isMe={u.id === me?.id}
            busy={setRole.isPending}
            onToggleRole={() => setRole.mutate({ id: u.id, role: u.role === "admin" ? "user" : "admin" })}
          />
        ))}
      </div>
    </div>
  );
}

function UserRow({ user, isMe, busy, onToggleRole }: { user: AdminUser; isMe: boolean; busy: boolean; onToggleRole: () => void }) {
  const isAdmin = user.role === "admin";
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "10px 12px",
      background: "var(--ma-cream)",
      border: "var(--ma-border) solid var(--ma-ink)",
      borderRadius: "var(--ma-card-radius)",
    }}>
      <div style={{
        width: 36, height: 36, flexShrink: 0,
        background: `var(--ma-${user.avatarTone || "brick"})`,
        color: "var(--ma-paper)",
        border: "var(--ma-border) solid var(--ma-ink)",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "var(--ma-display)", fontSize: 18, fontWeight: 800,
      }}>
        {user.avatarInitial}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontFamily: "var(--ma-body)", fontWeight: 600, fontSize: 14, color: "var(--ma-ink)" }}>
            {user.name}
          </span>
          {isAdmin && (
            <span className="ma-mono" style={{
              fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
              padding: "2px 6px", background: "var(--ma-brick)", color: "var(--ma-paper)",
              borderRadius: 2,
            }}>admin</span>
          )}
          {isMe && (
            <span className="ma-mono" style={{
              fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
              color: "var(--ma-ink-soft)",
            }}>you</span>
          )}
        </div>
        <div className="ma-mono" style={{ fontSize: 10, letterSpacing: "0.06em", color: "var(--ma-ink-soft)", marginTop: 2 }}>
          @{user.handle} · {user.email}
        </div>
      </div>
      <button
        onClick={onToggleRole}
        disabled={busy || (isMe && isAdmin)}
        title={isMe && isAdmin ? "Can't demote yourself" : undefined}
        style={{
          padding: "8px 12px", flexShrink: 0,
          background: isAdmin ? "transparent" : "var(--ma-ink)",
          color: isAdmin ? "var(--ma-ink)" : "var(--ma-paper)",
          border: "var(--ma-border) solid var(--ma-ink)",
          borderRadius: "var(--ma-card-radius)",
          fontFamily: "var(--ma-mono)", fontSize: 10, letterSpacing: "0.12em",
          textTransform: "uppercase", fontWeight: 600,
          cursor: (busy || (isMe && isAdmin)) ? "not-allowed" : "pointer",
          opacity: (busy || (isMe && isAdmin)) ? 0.4 : 1,
        }}
      >
        {isAdmin ? "demote" : "promote"}
      </button>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%", padding: "10px 12px",
  background: "var(--ma-cream)",
  border: "var(--ma-border) solid var(--ma-ink)",
  borderRadius: "var(--ma-card-radius)",
  fontFamily: "var(--ma-body)", fontSize: 16,
  color: "var(--ma-ink)", boxSizing: "border-box",
};
